import React from "react";

import { getTime } from "~/tools/date-time";
import type { Time } from "~/tools/date-time/time";
import { max } from "~/tools/max";
import type { Size } from "~/tools/size";
import { timeToPx } from "~/tools/time-to-px";
import { theme } from "~/tools/twin-migration";
import { usePermanent } from "~/tools/use-permanent";

import { Group, Line, useContainerSize } from "../../_canvas";
import { useTimelineRange, useUpdateFrozenTimeRangeOnChange } from "../_context";

import type { CommonTimelineBucketProps } from "./domain";

const GAP_BETWEEN_BUCKETS_PX = 3;
const BUCKET_DEFAULT_MIN_HEIGHT_PX = 3;
const BUCKET_DEFAULT_MAX_HEIGHT_PX = 23;
// assumes that 1000px wide screen fits ~100 pieces
const EMPTY_BUCKET_DEFAULT_WIDTH_PX = 10;
const EMPTY_BUCKET_HEIGHT_PX = 2;
const EMPTY_BUCKET_COLOR = theme("colors.gray.400");

export interface TimelineEventBucketsContainerItem<TBucket> {
	bucket: TBucket;
	x: number;
	width: number;
	height: number;
}

export interface TimelineEventBucketsContainerProps<TBucket> extends CommonTimelineBucketProps<TBucket> {
	listening?: boolean;
	renderBefore?: (bucketSize: Size) => React.ReactNode;
	getBucketStartTime: (bucket: TBucket) => Time;
	getBucketEndTime: (bucket: TBucket) => undefined | Time;
	children: (
		item: TimelineEventBucketsContainerItem<TBucket>,
		scaleCount: (count: number) => number,
	) => React.ReactNode;
}

export const TimelineEventBucketsContainer = <TBucket extends { count: number }>({
	listening,
	x,
	y,
	height: customHeight,
	width: customWidth,
	buckets,
	minBucketHeight = BUCKET_DEFAULT_MIN_HEIGHT_PX,
	maxBucketHeight: maxBucketHeightCustom,
	getBucketStartTime,
	getBucketEndTime,
	renderBefore,
	children,
}: TimelineEventBucketsContainerProps<TBucket>): React.ReactNode => {
	const [timelineStart, timelineEnd] = useTimelineRange();
	const mountTime = usePermanent(() => Date.now());
	const containerSize = useContainerSize();
	const width = customWidth ?? containerSize.width;
	const height = customHeight ?? containerSize.height;
	const maxBucketHeight = maxBucketHeightCustom ?? customHeight ?? BUCKET_DEFAULT_MAX_HEIGHT_PX;
	const maxBucketCount = React.useMemo(
		() =>
			max(
				0,
				buckets?.map(({ count }) => count),
			),
		[buckets],
	);

	const toPx = (time: Time) =>
		timeToPx({
			time,
			width,
			interval: { start: timelineStart, end: timelineEnd },
		});

	const scaleCount = (count: number) => {
		if (maxBucketCount === 0) {
			return 0;
		}

		return max(minBucketHeight, count * (maxBucketHeight / maxBucketCount));
	};

	const positionedBuckets = buckets?.map((bucket) => {
		const startTime = getBucketStartTime(bucket);
		const endTime = getBucketEndTime(bucket) ?? timelineEnd;
		const x = toPx(startTime);
		const width = toPx(endTime) - x - GAP_BETWEEN_BUCKETS_PX;

		return { bucket, x, width, height };
	});

	const firstBucket = positionedBuckets?.at(0) ?? {
		x: toPx(mountTime),
		width: EMPTY_BUCKET_DEFAULT_WIDTH_PX,
	};

	useUpdateFrozenTimeRangeOnChange([buckets]);

	return (
		<>
			{renderBefore?.({
				width: firstBucket.width,
				height: maxBucketHeight,
			})}

			<Group listening={listening} opacity={buckets == null ? 0.2 : 1} x={x} y={y}>
				{/* empty buckets before the first bucket */}
				<EmptyBucketsLine
					bucketWidth={firstBucket.width}
					end={0}
					start={firstBucket.x - GAP_BETWEEN_BUCKETS_PX}
					y={height}
				/>
				{/* empty buckets after the first bucket */}
				<EmptyBucketsLine bucketWidth={firstBucket.width} end={width} start={firstBucket.x} y={height} />

				{positionedBuckets
					?.filter(({ bucket }) => bucket.count > 0)
					.map((item) => (
						<React.Fragment key={getTime(getBucketStartTime(item.bucket))}>{children(item, scaleCount)}</React.Fragment>
					))}
			</Group>
		</>
	);
};

const EmptyBucketsLine: React.FC<{
	y: number;
	start: number;
	end: number;
	bucketWidth: number;
}> = ({ y, start, end, bucketWidth }) => (
	<Line
		dash={[bucketWidth, GAP_BETWEEN_BUCKETS_PX]}
		listening={false}
		points={[
			{ x: start, y: y - 1 },
			{ x: end, y: y - 1 },
		]}
		stroke={EMPTY_BUCKET_COLOR}
		strokeWidth={EMPTY_BUCKET_HEIGHT_PX}
	/>
);
